import { Component, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { RowNode, SelectionChangedEvent } from 'ag-grid-community';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { AliasList } from '../../../models/alias-lists';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DomainList } from '../../../models/domain-lists';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, HelpIndex } from '../../../models/enums';
import { AlignDirection, CellTemplateType, GenericColDef, GridContext, GridRowSelectionType, SortByFilter } from '../../../models/grid-context';
import { Profile } from '../../../models/profile';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { CreateEditEntityModalComponent } from '../create-edit-entity-modal/create-edit-entity-modal.component';
import { SYS_GENERATED_DOMAIN_LIST } from '../../../models/constants';

export enum AddSelectedTextToModalType {
    AliasList = 'AliasList',
    DomainList = 'DomainList',
    Profile = 'Profile',
}

export interface AddSelectedTextToModalData {
    type: AddSelectedTextToModalType;
    selectedText?: string;
    selectedDomains?: string[];
    isAddToExistingDomainList: boolean;
    projectHandle: string;
}

@Component({
    standalone: false,
    selector: 'res-add-selected-text-to-modal',
    templateUrl: './add-selected-text-to-modal.component.html',
    styleUrls: ['./add-selected-text-to-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AddSelectedTextToModalComponent extends BaseModalComponent implements OnInit {
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;

    modalType: AddSelectedTextToModalType;
    selectedText: string;
    selectedDomains: string[] = [];
    selectedItem: AliasList | DomainList | Profile;
    itemToSelect: AliasList | DomainList | Profile;
    addNewItemButtonText: string;
    noSelectionText: string;

    modalTitle: string;
    automationId = {
        modal: 'add-selected-text-to-modal',
        grid: '',
        customDomainListAutoId: 'domain-entries'
    };

    isBusy = false;
    isBusyMessage = '';
    isBusyDetails = false;
    isBusyMessageDetails = '';
    modalClass = 'add-selected-text-to';
    primaryButtonLabel = 'Save';
    secondaryButtonLabel = 'Cancel';
    helpIndex: HelpIndex;

    gridName: string;

    columnDefs = {
        left: new Array<GenericColDef>(),
        right: new Array<GenericColDef>()
    };
    rowSelection = GridRowSelectionType.single;
    requestInfo: DrRequestInfo;
    gridContext = new GridContext({
        sortBy: SortByFilter.NAME.toString(),
        sortAscending: true,
        sortEnabled: false
    });

    rowDisableRules = {
        'disabled-row': (params) => {
            return params.data.status === 'DUPLICATE';
        }
    };

    responseArrayCallback = (value: any): AliasList | DomainList | Profile => {
        switch (this.modalType) {
            case AddSelectedTextToModalType.AliasList: {
                return new AliasList(value);
            }
            case AddSelectedTextToModalType.DomainList: {
                return new DomainList(value);
            }
            case AddSelectedTextToModalType.Profile: {
                return new Profile(value);
            }
        }
    }
    onSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api && this.genericGrids.last.gridApi) {
            this.selectedItem = event.api.getSelectedRows()[0];
            if (this.selectedItem) {
                if (this.selectedItem instanceof AliasList) {
                    const selectedItemAliases = this.selectedItem.aliases ? this.selectedItem.aliases : [];
                    this.genericGrids.last.setData(this.mapListToStatus([this.selectedText], selectedItemAliases));
                } else if (this.selectedItem instanceof DomainList) {
                    this.isBusyMessageDetails = 'Loading Domain Entries...';
                    this.isBusyDetails = true;
                    this.restService.post(Endpoint.projectManager_listDomainEntries, { domainHandle: this.selectedItem.handle, requestHandle: this.selectedItem.projectHandle }).then(res => {
                        let domains = res && res.domainEntries && res.domainEntries.length > 0 ? res.domainEntries : [];
                        domains = [...new Set(domains)];
                        this.genericGrids.last.setData(this.mapListToStatus(this.selectedText ? [this.selectedText] : this.selectedDomains, domains));
                        this.isBusyDetails = false;
                    }, error => {
                        this.isBusyDetails = false;
                    });
                } else if (this.selectedItem instanceof Profile) {
                    this.isBusyMessageDetails = 'Loading Profile...';
                    this.isBusyDetails = true;
                    (this.selectedItem as Profile).content = this.selectedText + '\n\n';
                    this.restService.post(Endpoint.profileManager_getProfile, { handle: this.selectedItem.handle }).then(response => {
                        if (response && response.profile) {
                            (this.selectedItem as Profile).content += response.profile.content ? response.profile.content : '';
                            (this.selectedItem as Profile).lastComments = response.profile.lastComments ? response.profile.lastComments : '';
                        }
                        this.isBusyDetails = false;
                    }, error => {
                        this.isBusyDetails = false;
                    });
                }
            } else {
                this.genericGrids.last.gridApi.setGridOption('rowData', []);
            }
        }
    }
    loadDataFinishedCallback = () => {
        if (this.itemToSelect) {
            this.genericGrids.first.findNode((node: RowNode) => node.data.handle === this.itemToSelect.handle, true);
        } else if (this.selectedItem) {
            this.genericGrids.first.findNode((node: RowNode) => node.data.handle === this.selectedItem.handle, true);
        }
        if (this.modalType === AddSelectedTextToModalType.DomainList) {
            const nodesData = this.genericGrids.first.getData();
            const idxSysDomain = nodesData.findIndex(nd => nd.name === SYS_GENERATED_DOMAIN_LIST);
            if (idxSysDomain !== -1) {
                nodesData.splice(idxSysDomain, 1);
                this.genericGrids.first.setData(nodesData);
            }
        }
    }

    constructor(
        private modalService: BsModalService,
        private restService: RestService,
        private commonService: CommonService,
        private modalRef: BsModalRef,
    ) {
        super(modalService, modalRef);
    }

    mapListToStatus(newArray, existingArray) {
        const existingSet = new Set(existingArray);
        const list = [];

        newArray = newArray.sort();
        existingArray = existingArray.sort();
        // Process new array
        newArray.forEach(value => {
            const status = existingSet.has(value) ? 'DUPLICATE' : 'TO BE ADDED';
            const className = status === 'DUPLICATE' ? "duplicate-class" : status === 'TO BE ADDED' ? 'to-be-added-class' : '';
            list.push({ value, status, className });
        });

        // Process existing array
        existingArray.forEach(value => {
            const status = 'EXISTING';
            list.push({ value, status, className: "existing-class" });
        });

        // Sort list by status
        const statusOrder = ["TO BE ADDED", "DUPLICATE", "EXISTING"];
        list.sort((a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status));

        return list;
    }

    ngOnInit(): void {
        const addSelectedTextToModalData = this.modalData as AddSelectedTextToModalData;
        if (addSelectedTextToModalData && (addSelectedTextToModalData.selectedText || addSelectedTextToModalData.selectedDomains) && addSelectedTextToModalData.type) {
            if (addSelectedTextToModalData.selectedText) {
                this.selectedText = addSelectedTextToModalData.selectedText;
            } else {
                this.selectedDomains = addSelectedTextToModalData.selectedDomains;
            }
            this.modalType = addSelectedTextToModalData.type;
            this.helpIndex = this.selectedText ? HelpIndex.myedisc_select_profile : HelpIndex.add_to_domain_list_dlg;
            switch (this.modalType) {
                case AddSelectedTextToModalType.AliasList: {
                    this.addNewItemButtonText = 'New Alias List ...';
                    this.noSelectionText = 'Please select an alias list';
                    this.gridName = 'Alias Lists';
                    this.modalTitle = 'Add Text to Alias List';
                    this.automationId.grid = 'alias-lists';
                    this.columnDefs.left.push(
                        <GenericColDef>{
                            headerName: 'Alias List Name',
                            field: 'name',
                            automationId: this.automationId.grid,
                            defaultSortAscending: true,
                            defaultSortBy: SortByFilter.NAME,
                            lockVisible: true
                        },
                        <GenericColDef>{
                            headerName: 'Description',
                            field: 'description',
                            automationId: this.automationId.grid
                        });
                    this.columnDefs.right.push(<GenericColDef>{
                        headerName: 'Alias Name',
                        valueGetter: (params: any) => params.data,
                        automationId: this.automationId.grid,
                        defaultSortAscending: true,
                        defaultSortBy: SortByFilter.NAME,
                        cellTemplateType: CellTemplateType.textWithStatus,
                        lockVisible: true
                    });
                    this.requestInfo = new DrRequestInfo({
                        endpoint: Endpoint.projectManager_listAliasList,
                        responseName: 'aliasList',
                        data: { handle: this.commonService.currentProject.handle }
                    });
                    break;
                }
                case AddSelectedTextToModalType.DomainList: {
                    this.addNewItemButtonText = 'New Domain List ...';
                    this.noSelectionText = 'Please select a domain list';
                    this.gridName = 'Domain Lists';
                    this.modalTitle = this.selectedText ? 'Add Text to Domain List' : 'Add to Domain List';
                    this.automationId.grid = 'domain-lists';
                    this.columnDefs.left.push(
                        <GenericColDef>{
                            headerName: 'Domain List Name',
                            field: 'name',
                            automationId: this.automationId.grid,
                            defaultSortAscending: true,
                            defaultSortBy: SortByFilter.NAME,
                            lockVisible: true,
                        },
                        <GenericColDef>{
                            headerName: 'Description',
                            field: 'description',
                            automationId: this.automationId.grid
                        });
                    this.columnDefs.right.push(<GenericColDef>{
                        headerName: 'Domain Name',
                        automationId: this.automationId.grid,
                        defaultSortAscending: true,
                        defaultSortBy: SortByFilter.NAME,
                        lockVisible: true,
                        cellTemplateType: CellTemplateType.textWithStatus
                    });
                    this.requestInfo = new DrRequestInfo({
                        endpoint: Endpoint.projectManager_listDomains,
                        responseName: 'domains',
                        data: { projectHandle: this.commonService.currentProject?.handle || this.modalData?.projectHandle }
                    });
                    break;
                }
                case AddSelectedTextToModalType.Profile: {
                    this.addNewItemButtonText = 'New Synthetic Document ...';
                    this.noSelectionText = 'Please select a synthetic document';
                    this.gridName = 'Synthetic Document';
                    this.modalTitle = 'Add Text to Synthetic Document';
                    this.automationId.grid = 'synthetic-document-summary';
                    this.columnDefs.left.push(
                        <GenericColDef>{
                            headerName: 'Synthetic Document Name',
                            field: 'name',
                            automationId: this.automationId.grid,
                            sortBy: SortByFilter.NAME,
                            lockVisible: true
                        },
                        <GenericColDef>{
                            headerName: 'Description',
                            field: 'description',
                            automationId: this.automationId.grid
                        },
                        <GenericColDef>{
                            headerName: 'Date Created',
                            field: 'createdOn',
                            automationId: this.automationId.grid,
                            headerAlignDirection: AlignDirection.right,
                            cellTemplateType: CellTemplateType.date,
                            cellAlignDirection: AlignDirection.right,
                            width: 147,
                            suppressSizeToFit: true
                        });
                    this.requestInfo = new DrRequestInfo({
                        endpoint: Endpoint.projectManager_listProfiles,
                        responseName: 'profiles',
                        data: { handle: this.commonService.currentProject.handle }
                    });
                    break;
                }
            }
            if (this.genericGrids && this.genericGrids.first) {
                this.genericGrids.first.loadData();
            }
        }
    }

    addNewItem(): void {
        let modalData: any;
        switch (this.modalType) {
            case AddSelectedTextToModalType.AliasList: {
                modalData = new AliasList({ parentHandle: this.commonService.currentProject.handle });
                break;
            }
            case AddSelectedTextToModalType.DomainList: {
                modalData = new DomainList({ projectHandle: this.commonService.currentProject?.handle || this.modalData?.projectHandle })
                break;
            }
            case AddSelectedTextToModalType.Profile: {
                modalData = new Profile({ projectHandle: this.commonService.currentProject.handle });
                break;
            }
        }
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: modalData }).then((response) => {
            if (response && response.modalData) {
                switch (this.modalType) {
                    case AddSelectedTextToModalType.AliasList: {
                        this.itemToSelect = response.modalData.aliasList;
                        break;
                    }
                    case AddSelectedTextToModalType.DomainList: {
                        this.itemToSelect = response.modalData.domain;
                        break;
                    }
                    case AddSelectedTextToModalType.Profile: {
                        this.itemToSelect = response.modalData.profile;
                        break;
                    }
                }
                this.genericGrids.first.loadData();
            }
        });
    }

    isPrimaryButtonEnabled(): boolean {
        return Boolean(this.selectedItem && (this.selectedText || this.selectedDomains.length > 0));
    }

    ok(): void {
        if (this.selectedItem && (this.selectedText || this.selectedDomains.length > 0)) {
            let endpoint: Endpoint;
            let request: any;
            switch (this.modalType) {
                case AddSelectedTextToModalType.AliasList: {
                    const aliases = [...new Set(this.genericGrids.last.getData().map(alias => alias.value))];
                    this.isBusyMessage = 'Updating Alias List...';
                    endpoint = Endpoint.projectManager_updateAliasList;
                    request = {
                        handle: this.selectedItem.handle,
                        name: this.selectedItem.name,
                        description: this.selectedItem.description,
                        aliases: aliases
                    };
                    break;
                }
                case AddSelectedTextToModalType.DomainList: {
                    const domainEntries = [...new Set(this.genericGrids.last.getData().map(domains => domains.value))];
                    this.isBusyMessage = 'Updating Domain List...';
                    endpoint = Endpoint.projectManager_updateDomain;
                    request = {
                        handle: this.selectedItem.handle,
                        name: this.selectedItem.name,
                        description: this.selectedItem.description,
                        domainEntries: domainEntries,
                        updateOperation: 'ADD_ENTRIES'
                    };
                    break;
                }
                case AddSelectedTextToModalType.Profile: {
                    this.isBusyMessage = 'Updating Synthetic Document...';
                    endpoint = Endpoint.profileManager_setContent;
                    request = {
                        handle: this.selectedItem.handle,
                        content: (this.selectedItem as Profile).content,
                        comments: (this.selectedItem as Profile).lastComments,
                        taskDescription: 'Setting synthetic document content for ' + this.selectedItem.name
                    };
                    break;
                }
            }
            if (endpoint && request) {
                this.isBusy = true;
                this.restService.post(endpoint, request, true, true);
                this.isBusy = false;
                this.closeModal(true);
            }
        }
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);
}
